"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { emailOtpLogin, googleLogin } from "@/app/actions/actions";
import { useState, useEffect } from "react";
import { z } from "zod";
import { useRouter, useParams } from "next/navigation";
import { useFormStatus } from "react-dom";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { usePostHogDistinctId } from "@/hooks/usePostHogDistinctId";
import { getLoginBrowserClass, type LoginBrowserClass } from "@/utils/tracking/loginBrowserClass";
const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});
async function hashEmail(email: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}
interface EmailOtpResponse {
  error?: string;
  errorCode?: string;
  success?: boolean;
  message?: string;
  next?: string;
  retryAfterSeconds?: number;
}
interface SubmitButtonProps {
  emailButtonSending: string;
  emailButton: string;
}
function SubmitButton(t0) {
  const $ = _c(3);
  const {
    emailButtonSending,
    emailButton
  } = t0;
  const {
    pending
  } = useFormStatus();
  const t1 = pending ? emailButtonSending : emailButton;
  let t2;
  if ($[0] !== pending || $[1] !== t1) {
    t2 = <Button type="submit" className="w-full" disabled={pending}>{t1}</Button>;
    $[0] = pending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
interface LoginFormProps extends React.ComponentPropsWithoutRef<"div"> {
  tiktokId?: string;
  prefillEmail?: string;
  nextPath?: string;
}
export function LoginForm(t0) {
  const $ = _c(88);
  let className;
  let nextPath;
  let prefillEmail;
  let props;
  let tiktokId;
  if ($[0] !== t0) {
    ({
      className,
      tiktokId,
      prefillEmail,
      nextPath,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = nextPath;
    $[3] = prefillEmail;
    $[4] = props;
    $[5] = tiktokId;
  } else {
    className = $[1];
    nextPath = $[2];
    prefillEmail = $[3];
    props = $[4];
    tiktokId = $[5];
  }
  const [emailError, setEmailError] = useState(null);
  const [isEmbeddedBrowser, setIsEmbeddedBrowser] = useState(false);
  const [browserClass, setBrowserClass] = useState("");
  const router = useRouter();
  const t = useTranslations("Login");
  const params = useParams();
  usePostHog();
  const posthogId = usePostHogDistinctId();
  const locale = typeof params.locale === "string" ? params.locale : "en";
  let t1;
  if ($[6] !== t) {
    t1 = function getEmailError(errorCode) {
      switch (errorCode) {
        case "rateLimited":
          {
            return t("errors.rateLimited");
          }
        case "invalidEmail":
          {
            return t("errors.invalidEmail");
          }
        default:
          {
            return t("errors.generic");
          }
      }
    };
    $[6] = t;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const getEmailError = t1;
  let t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsEmbeddedBrowser(/FBAN|FBAV|FBMS|FB_IAB|FB4A|FBAN\/Messenger|Instagram|WhatsApp/.test(window.navigator.userAgent));
      setBrowserClass(getLoginBrowserClass(window.navigator.userAgent));
    };
    t3 = [];
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== getEmailError || $[11] !== locale || $[12] !== router || $[13] !== t) {
    t4 = async function handleEmailSubmit(formData) {
      const email = formData.get("email");
      const tiktokId_0 = formData.get("tiktokId")?.toString();
      const fbq = typeof window !== "undefined" ? window.fbq : undefined;
      if (fbq) {
        hashEmail(email as string).then(hashedEmail => {
          fbq("track", "CompleteRegistration", {
            content_name: "Login Form",
            content_category: "User Registration"
          }, {
            eventID: hashedEmail
          });
        });
      }
      const ttq = typeof window !== "undefined" ? window.ttq : undefined;
      if (ttq) {
        hashEmail(email as string).then(hashedEmail_0 => {
          ttq.identify({
            email: hashedEmail_0
          });
          ttq.track("CompleteRegistration", {
            content_name: "Login Form",
            content_category: "User Registration"
          }, tiktokId_0 ? {
            callback: tiktokId_0
          } : {});
        });
      }
      ;
      try {
        emailSchema.parse({
          email
        });
        setEmailError(null);
        const result = (await emailOtpLogin(formData)) as EmailOtpResponse;
        if (result.error) {
          setEmailError(getEmailError(result.errorCode));
        } else {
          if (result.success) {
            const activateParams = new URLSearchParams();
            if (result.next) {
              activateParams.set("next", result.next);
            }
            if (result.retryAfterSeconds) {
              activateParams.set("resendAt", String(Date.now() + result.retryAfterSeconds * 1000));
            }
            router.push(`/${locale}/login/activate?${activateParams.toString()}`);
          }
        }
      } catch (t5) {
        const error = t5;
        if (error instanceof z.ZodError) {
          setEmailError(t("errors.invalidEmail"));
        } else {
          setEmailError(t("errors.generic"));
        }
      }
    };
    $[10] = getEmailError;
    $[11] = locale;
    $[12] = router;
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleEmailSubmit = t4;
  let t5;
  if ($[15] !== className) {
    t5 = cn("flex flex-col gap-6", className);
    $[15] = className;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t) {
    t6 = t("title");
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t6) {
    t7 = <CardTitle className="text-xl">{t6}</CardTitle>;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t) {
    t8 = t("subtitle");
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <CardDescription>{t8}</CardDescription>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t9) {
    t10 = <CardHeader className="text-center">{t7}{t9}</CardHeader>;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== isEmbeddedBrowser || $[29] !== locale || $[30] !== nextPath || $[31] !== posthogId || $[32] !== t) {
    t11 = !isEmbeddedBrowser && <><form action={googleLogin}><input type="hidden" name="locale" value={locale} /><input type="hidden" name="posthogId" value={posthogId} />{nextPath && <input type="hidden" name="next" value={nextPath} />}<Button variant="outline" className="w-full" type="submit"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 mr-2"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" fill="currentColor" /></svg>{t("googleButton")}</Button></form><div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border"><span className="relative z-10 bg-background px-2 text-muted-foreground">{t("or")}</span></div></>;
    $[28] = isEmbeddedBrowser;
    $[29] = locale;
    $[30] = nextPath;
    $[31] = posthogId;
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Label htmlFor="email">Email</Label>;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const t13 = prefillEmail || "";
  const t14 = emailError && "border-destructive";
  let t15;
  if ($[35] !== t14) {
    t15 = cn(t14);
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t15) {
    t16 = <Input id="email" name="email" type="email" placeholder="m@example.com" defaultValue={t13} required={true} className={t15} />;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== emailError) {
    t17 = emailError && <p className="text-sm text-destructive">{emailError}</p>;
    $[40] = emailError;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t16 || $[43] !== t17) {
    t18 = <div className="grid gap-2">{t12}{t16}{t17}</div>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== locale) {
    t19 = <input type="hidden" name="locale" value={locale} />;
    $[45] = locale;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== posthogId) {
    t20 = <input type="hidden" name="posthogId" value={posthogId} />;
    $[47] = posthogId;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== browserClass) {
    t21 = <input type="hidden" name="browserClass" value={browserClass} />;
    $[49] = browserClass;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== nextPath) {
    t22 = nextPath && <input type="hidden" name="next" value={nextPath} />;
    $[51] = nextPath;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== tiktokId) {
    t23 = tiktokId && <input type="hidden" name="tiktokId" value={tiktokId} />;
    $[53] = tiktokId;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t) {
    t24 = t("emailButtonSending");
    $[55] = t;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t) {
    t25 = t("emailButton");
    $[57] = t;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t24 || $[60] !== t25) {
    t26 = <SubmitButton emailButtonSending={t24} emailButton={t25} />;
    $[59] = t24;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21 || $[66] !== t22 || $[67] !== t23 || $[68] !== t26) {
    t27 = <div className="grid gap-6">{t18}{t19}{t20}{t21}{t22}{t23}{t26}</div>;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== handleEmailSubmit || $[71] !== t27) {
    t28 = <form action={handleEmailSubmit}>{t27}</form>;
    $[70] = handleEmailSubmit;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t11 || $[74] !== t28) {
    t29 = <CardContent><div className="grid gap-6">{t11}{t28}</div></CardContent>;
    $[73] = t11;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t10 || $[77] !== t29) {
    t30 = <Card>{t10}{t29}</Card>;
    $[76] = t10;
    $[77] = t29;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== t) {
    t31 = t.rich("terms", {
      termsLink: _temp,
      privacyLink: _temp2
    });
    $[79] = t;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== t31) {
    t32 = <div className="text-balance text-center text-xs text-muted-foreground">{t31}</div>;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== props || $[84] !== t30 || $[85] !== t32 || $[86] !== t5) {
    t33 = <div className={t5} {...props}>{t30}{t32}</div>;
    $[83] = props;
    $[84] = t30;
    $[85] = t32;
    $[86] = t5;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  return t33;
}
function _temp2(chunks_0) {
  return <a href="/privacy-policy" target="_blank" className="underline underline-offset-4 hover:text-primary">{chunks_0}</a>;
}
function _temp(chunks) {
  return <a href="/terms-and-conditions" target="_blank" className="underline underline-offset-4 hover:text-primary">{chunks}</a>;
}